import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  vipClubs,
  vipClubsSummary,
  statusLabels,
  statusColors,
  type VipClub,
  type VipClubStatus,
} from '@/mocks/vipClubs';

type FilterTab = 'todas' | VipClubStatus;

export default function ClubVipPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [filterTab, setFilterTab] = useState<FilterTab>('todas');
  const [selectedClub, setSelectedClub] = useState<VipClub | null>(null);

  const filteredClubs = filterTab === 'todas'
    ? vipClubs
    : vipClubs.filter((c) => c.status === filterTab);

  const activeClubs = vipClubs.filter((c) => c.status === 'active');

  const clubCounts = {
    todas: vipClubs.length,
    active: vipClubs.filter((c) => c.status === 'active').length,
    coming_soon: vipClubs.filter((c) => c.status === 'coming_soon').length,
    inactive: vipClubs.filter((c) => c.status === 'inactive').length,
  };

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">

          {/* ── HERO ── */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-56 md:h-64 bg-gradient-to-br from-amber-600/30 via-yellow-500/25 to-amber-400/15 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/25"></div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-300/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
                <div className="w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-4">
                  <i className="ri-vip-crown-line text-background-50 text-3xl"></i>
                </div>
                <h1 className="text-2xl md:text-4xl font-black text-foreground-950 mb-2">Club VIP RFW</h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-lg leading-relaxed">
                  Membresías exclusivas con contenido premium, eventos privados y beneficios que ninguna otra plataforma de radio ofrece.
                </p>
                <div className="flex items-center gap-3 mt-5">
                  <span className="px-3 py-1 rounded-full bg-amber-100/80 text-amber-800 text-[11px] font-bold">
                    {vipClubsSummary.activeClubs} clubes activos
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-50/50 text-foreground-700 text-[11px] font-bold">
                    {vipClubsSummary.totalMembers.toLocaleString()}+ miembros
                  </span>
                </div>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-background-200">
              {[
                { label: 'Contenido Exclusivo', value: vipClubsSummary.totalExclusiveContent, icon: 'ri-play-circle-line', color: 'text-rose-600' },
                { label: 'Eventos Privados', value: vipClubsSummary.totalExclusiveEvents, icon: 'ri-calendar-check-line', color: 'text-amber-600' },
                { label: 'Sorteos VIP', value: vipClubsSummary.totalGiveaways, icon: 'ri-gift-line', color: 'text-emerald-600' },
                { label: 'Premios Entregados', value: vipClubsSummary.totalPrizes, icon: 'ri-trophy-line', color: 'text-violet-600' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-lg font-extrabold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── FILTER TABS ── */}
          <div className="mt-6 flex items-center gap-1 bg-background-100 rounded-full p-1 max-w-md mx-auto">
            {([
              { key: 'todas' as FilterTab, label: 'Todos' },
              { key: 'active' as FilterTab, label: 'Activos' },
              { key: 'coming_soon' as FilterTab, label: 'Próximamente' },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilterTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  filterTab === tab.key
                    ? 'bg-amber-500 text-background-50 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  filterTab === tab.key ? 'bg-background-50/20' : 'bg-background-200'
                }`}>
                  {clubCounts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {/* ── MEMBERSHIP CARDS GRID ── */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClubs.map((club) => {
              const statusInfo = statusColors[club.status];
              const freqSlug = club.radioFrequency.replace(/\./g, '-').replace(/\s+/g, '').toLowerCase();
              return (
                <div
                  key={club.id}
                  className={`bg-background-50 rounded-2xl overflow-hidden transition-all group ${
                    club.status === 'active'
                      ? 'hover:-translate-y-1 hover:shadow-sm cursor-pointer'
                      : club.status === 'coming_soon'
                        ? 'opacity-75'
                        : 'opacity-60'
                  }`}
                  onClick={() => club.status === 'active' && setSelectedClub(club)}
                >
                  {/* Card Header */}
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={club.thumbnailUrl}
                      alt={club.clubName}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
                    {/* Status badge */}
                    <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold border ${statusInfo.badge}`}>
                      <span className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`}></span>
                        <span className={statusInfo.text}>{statusLabels[club.status]}</span>
                      </span>
                    </div>
                    {/* Radio name */}
                    <div className="absolute bottom-3 left-4">
                      <span className="text-[10px] text-foreground-500 font-medium">{club.radioName}</span>
                      <span className="text-[10px] text-foreground-400 ml-1.5">{club.radioFrequency}</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-4 pb-4">
                    <h3 className="text-sm font-extrabold text-foreground-950 mb-1">{club.clubName}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">{club.description}</p>

                    {/* Price & Members */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        {club.monthlyPrice === 0 ? (
                          <span className="text-sm font-extrabold text-emerald-600">Gratis</span>
                        ) : (
                          <span className="text-sm font-extrabold text-foreground-950">
                            ${club.monthlyPrice}
                            <span className="text-[10px] font-medium text-foreground-400">/mes</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-user-line text-foreground-400"></i>
                        {club.membersCount.toLocaleString()}
                        {club.memberLimit > 0 && (
                          <span className="text-foreground-300">/ {club.memberLimit.toLocaleString()}</span>
                        )}
                      </div>
                    </div>

                    {/* Benefits pills */}
                    <div className="flex flex-wrap gap-1.5">
                      {club.benefits.exclusiveContent && (
                        <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded-md text-[10px] font-semibold">
                          {club.benefits.exclusiveContentCount} contenidos
                        </span>
                      )}
                      {club.benefits.exclusiveEvents && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md text-[10px] font-semibold">
                          {club.benefits.exclusiveEventsCount} eventos
                        </span>
                      )}
                      {club.benefits.giveaways && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-[10px] font-semibold">
                          {club.benefits.giveawaysCount} sorteos
                        </span>
                      )}
                      {club.benefits.prizes && (
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-md text-[10px] font-semibold">
                          {club.benefits.prizesCount} premios
                        </span>
                      )}
                    </div>

                    {/* Engagement gauge */}
                    {club.status === 'active' && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-foreground-500">Engagement</span>
                          <span className="text-[10px] font-bold text-foreground-700">{club.engagementRate}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 rounded-full transition-all duration-700"
                            style={{ width: `${club.engagementRate}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {club.status === 'active' && (
                      <div className="mt-3 flex items-center gap-1 text-xs text-amber-700 font-medium">
                        <i className="ri-arrow-right-circle-line"></i>
                        Ver detalles
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── BENEFITS OVERVIEW ── */}
          <div className="mt-10">
            <div className="text-center mb-6">
              <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Beneficios del Club VIP</h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                Ser miembro VIP te da acceso a experiencias que ningún oyente regular tiene.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  icon: 'ri-play-circle-fill',
                  title: 'Contenido Exclusivo',
                  desc: 'Playlists secretas, sesiones acústicas inéditas, detrás de cámaras y podcasts solo para miembros.',
                  color: 'bg-rose-500',
                  stats: `${vipClubsSummary.totalExclusiveContent} piezas disponibles`,
                },
                {
                  icon: 'ri-calendar-check-fill',
                  title: 'Eventos Privados',
                  desc: 'Conciertos privados, meet & greets con artistas, cenas exclusivas y acceso preferencial a festivales.',
                  color: 'bg-amber-500',
                  stats: `${vipClubsSummary.totalExclusiveEvents} eventos este mes`,
                },
                {
                  icon: 'ri-gift-fill',
                  title: 'Sorteos VIP',
                  desc: 'Participación en rifas exclusivas con premios que no están disponibles para el público general.',
                  color: 'bg-emerald-500',
                  stats: `${vipClubsSummary.totalGiveaways} sorteos activos`,
                },
                {
                  icon: 'ri-trophy-fill',
                  title: 'Premios Premium',
                  desc: 'Desde productos tecnológicos hasta viajes, entradas VIP y coleccionables de edición limitada.',
                  color: 'bg-violet-500',
                  stats: `${vipClubsSummary.totalPrizes} premios entregados`,
                },
              ].map((benefit) => (
                <div key={benefit.title} className="bg-background-50 rounded-2xl p-5 text-center">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${benefit.color}`}>
                    <i className={`${benefit.icon} text-background-50 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-extrabold text-foreground-950 mb-1.5">{benefit.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-3">{benefit.desc}</p>
                  <span className="px-3 py-1 bg-background-100 rounded-full text-[10px] font-bold text-foreground-600">
                    {benefit.stats}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── FEATURED CLUBS ── */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold text-foreground-950">Clubes Destacados</h2>
              <Link to="/top-fans" className="text-xs text-amber-700 font-medium hover:text-amber-800 transition-colors cursor-pointer flex items-center gap-1">
                <i className="ri-star-line"></i>
                Ver Top Fans
              </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activeClubs.slice(0, 4).map((club) => {
                const radio = {
                  slug: club.radioFrequency.replace(/\./g, '-').replace(/\s+/g, '').toLowerCase(),
                  name: club.radioName,
                  freq: club.radioFrequency,
                };
                return (
                  <div key={club.id} className="bg-background-50 rounded-2xl p-5 flex flex-col sm:flex-row gap-4">
                    <img
                      src={club.thumbnailUrl}
                      alt={club.clubName}
                      className="w-full sm:w-28 h-32 sm:h-28 rounded-xl object-cover object-top shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h3 className="text-sm font-extrabold text-foreground-950">{club.clubName}</h3>
                          <Link
                            to={`/radio/${radio.slug}`}
                            className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                          >
                            {radio.name} · {radio.freq}
                          </Link>
                        </div>
                        {club.monthlyPrice === 0 ? (
                          <span className="text-xs font-extrabold text-emerald-600 bg-emerald-100 px-2.5 py-1 rounded-lg">Gratis</span>
                        ) : (
                          <span className="text-xs font-extrabold text-foreground-950 bg-amber-100 px-2.5 py-1 rounded-lg">
                            ${club.monthlyPrice}/mes
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">{club.description}</p>

                      {/* Mini benefits */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {club.benefits.exclusiveContent && (
                          <span className="px-2 py-0.5 bg-rose-50 text-rose-600 rounded-md text-[10px] font-semibold">
                            <i className="ri-play-circle-line mr-0.5"></i>{club.benefits.exclusiveContentCount}
                          </span>
                        )}
                        {club.benefits.exclusiveEvents && (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[10px] font-semibold">
                            <i className="ri-calendar-check-line mr-0.5"></i>{club.benefits.exclusiveEventsCount}
                          </span>
                        )}
                        {club.benefits.giveaways && (
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md text-[10px] font-semibold">
                            <i className="ri-gift-line mr-0.5"></i>{club.benefits.giveawaysCount}
                          </span>
                        )}
                      </div>

                      {/* Recent events or prizes */}
                      {(club.recentEvents.length > 0 || club.recentPrizes.length > 0) && (
                        <div>
                          {club.recentEvents.slice(0, 1).map((ev) => (
                            <div key={ev.name} className="text-[10px] text-foreground-500 flex items-center gap-1">
                              <i className="ri-calendar-line text-foreground-400"></i>
                              Próximo: {ev.name}
                            </div>
                          ))}
                          {club.recentPrizes.slice(0, 1).map((pr) => (
                            <div key={pr.name} className="text-[10px] text-foreground-500 flex items-center gap-1">
                              <i className="ri-user-star-line text-foreground-400"></i>
                              Último ganador: {pr.winner} — {pr.name}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
                        <span className="text-xs text-foreground-500">{club.membersCount.toLocaleString()} miembros</span>
                        <span className="text-xs text-amber-600 font-semibold">{club.engagementRate}% engagement</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── HOW IT WORKS ── */}
          <div className="mt-10 bg-background-50 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-extrabold text-foreground-950 text-center mb-6">¿Cómo funciona?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                {
                  step: '1',
                  icon: 'ri-search-line',
                  title: 'Elegí tu club',
                  desc: 'Explorá los clubes disponibles por radio, categoría y precio. Cada radio ofrece distintos niveles de membresía.',
                },
                {
                  step: '2',
                  icon: 'ri-vip-crown-line',
                  title: 'Activá tu membresía',
                  desc: 'Seleccioná el plan que más te guste. Hay opciones gratuitas y premium con diferentes beneficios.',
                },
                {
                  step: '3',
                  icon: 'ri-rocket-line',
                  title: 'Disfrutá los beneficios',
                  desc: 'Accedé a contenido exclusivo, participá en sorteos VIP y asistí a eventos que solo los miembros pueden ver.',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <i className={`${item.icon} text-amber-600 text-2xl`}></i>
                  </div>
                  <div className="w-7 h-7 mx-auto -mt-5 mb-3 rounded-full bg-amber-500 flex items-center justify-center relative z-10">
                    <span className="text-xs font-extrabold text-background-50">{item.step}</span>
                  </div>
                  <h3 className="text-sm font-extrabold text-foreground-950 mb-1">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── CTA ── */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
              <i className="ri-vip-crown-line text-background-50 text-3xl"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950 mb-2">¿Listo para ser VIP?</h2>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Unite a la comunidad más exclusiva de RadioFrecuenciaWEB. Contenido premium, eventos privados y beneficios que transforman tu experiencia radial.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/gamificacion"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                Ver Gamificación
              </Link>
              <Link
                to="/top-fans"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-line mr-1.5"></i>
                Top Fans
              </Link>
            </div>
          </div>

          {/* ── Cross-links ── */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to="/insignias"
              className="bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                  <i className="ri-award-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Insignias</h4>
                  <p className="text-xs text-foreground-500">Colección de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/niveles"
              className="bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                  <i className="ri-stack-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Niveles</h4>
                  <p className="text-xs text-foreground-500">Sistema de progresión</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/eventos"
              className="bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
                  <i className="ri-calendar-event-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Eventos</h4>
                  <p className="text-xs text-foreground-500">Próximos eventos</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>

      {/* ── DETAIL MODAL ── */}
      {selectedClub && (
        <ClubDetailModal club={selectedClub} onClose={() => setSelectedClub(null)} />
      )}
    </div>
  );
}

/* ───── Club Detail Modal ───── */
function ClubDetailModal({ club, onClose }: { club: VipClub; onClose: () => void }) {
  const statusInfo = statusColors[club.status];
  const radio = {
    slug: club.radioFrequency.replace(/\./g, '-').replace(/\s+/g, '').toLowerCase(),
    name: club.radioName,
    freq: club.radioFrequency,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-background-50 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-40 overflow-hidden rounded-t-2xl">
          <img src={club.thumbnailUrl} alt={club.clubName} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-background-50 text-foreground-700 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm"
          >
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute bottom-4 left-5">
            <h2 className="text-xl font-extrabold text-foreground-950">{club.clubName}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Link
                to={`/radio/${radio.slug}`}
                className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                onClick={onClose}
              >
                {radio.name} · {radio.freq}
              </Link>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusInfo.badge} ${statusInfo.text}`}>
                {statusLabels[club.status]}
              </span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pb-6">
          <p className="text-sm text-foreground-600 leading-relaxed mb-4">{club.description}</p>

          {/* Price & Members */}
          <div className="flex items-center justify-between bg-background-100 rounded-xl p-4 mb-5">
            <div>
              <div className="text-xs text-foreground-500 mb-0.5">Precio mensual</div>
              {club.monthlyPrice === 0 ? (
                <span className="text-xl font-extrabold text-emerald-600">Gratis</span>
              ) : (
                <span className="text-xl font-extrabold text-foreground-950">
                  ${club.monthlyPrice}
                  <span className="text-xs font-medium text-foreground-400">/mes</span>
                </span>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-foreground-500 mb-0.5">Miembros</div>
              <span className="text-lg font-extrabold text-foreground-950">{club.membersCount.toLocaleString()}</span>
              {club.memberLimit > 0 && (
                <span className="text-xs text-foreground-400"> / {club.memberLimit.toLocaleString()} cupos</span>
              )}
            </div>
          </div>

          {/* Benefits */}
          <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-gift-line text-amber-500"></i> Beneficios incluidos
          </h4>
          <div className="grid grid-cols-2 gap-2 mb-5">
            {club.benefits.exclusiveContent && (
              <div className="bg-rose-50 rounded-xl p-3">
                <div className="text-lg font-extrabold text-rose-700">{club.benefits.exclusiveContentCount}</div>
                <div className="text-[10px] text-rose-600 font-medium">Contenidos exclusivos</div>
              </div>
            )}
            {club.benefits.exclusiveEvents && (
              <div className="bg-amber-50 rounded-xl p-3">
                <div className="text-lg font-extrabold text-amber-700">{club.benefits.exclusiveEventsCount}</div>
                <div className="text-[10px] text-amber-600 font-medium">Eventos privados</div>
              </div>
            )}
            {club.benefits.giveaways && (
              <div className="bg-emerald-50 rounded-xl p-3">
                <div className="text-lg font-extrabold text-emerald-700">{club.benefits.giveawaysCount}</div>
                <div className="text-[10px] text-emerald-600 font-medium">Sorteos VIP</div>
              </div>
            )}
            {club.benefits.prizes && (
              <div className="bg-violet-50 rounded-xl p-3">
                <div className="text-lg font-extrabold text-violet-700">{club.benefits.prizesCount}</div>
                <div className="text-[10px] text-violet-600 font-medium">Premios</div>
              </div>
            )}
          </div>

          {/* Recent Events */}
          {club.recentEvents.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-extrabold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-calendar-check-line text-amber-500"></i> Eventos recientes
              </h4>
              <div className="space-y-2">
                {club.recentEvents.map((ev) => (
                  <div key={ev.name} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <i className="ri-calendar-line text-amber-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{ev.name}</div>
                      <div className="text-xs text-foreground-500">{new Date(ev.date).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Prizes */}
          {club.recentPrizes.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-extrabold text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-trophy-line text-amber-500"></i> Premios entregados
              </h4>
              <div className="space-y-2">
                {club.recentPrizes.map((pr) => (
                  <div key={pr.name} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                      <i className="ri-user-star-line text-violet-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{pr.name}</div>
                      <div className="text-xs text-foreground-500">Ganador: {pr.winner}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Engagement */}
          <div className="bg-background-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground-700">Tasa de Engagement</span>
              <span className="text-sm font-extrabold text-amber-600">{club.engagementRate}%</span>
            </div>
            <div className="w-full h-2.5 bg-background-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full"
                style={{ width: `${club.engagementRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}